import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wallet, ExternalLink } from "lucide-react";
import { ComposedChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, formatCurrency, dec } from "@/lib/formatters";
import { tooltipStyle, gruppeInfo } from "./oversiktConstants";

const mill = (v) => (v == null ? "—" : `${formatNumber(Math.round(v / 1000))} mill. kr`);
const f1 = (v) => (v == null ? "—" : dec(v, 1));
const f2 = (v) => (v == null ? "—" : dec(v, 2));

const AvgLines = ({ rows, land, snitt, snittKey, suffix, field, format, testId, title }) => {
  const at = (list, y) => list?.find((r) => r.year === y)?.[field] ?? null;
  const years = [...new Set([...rows, ...(land || [])].map((r) => r.year))].sort();
  const chart = years.map((y) => ({ year: y, Kommunen: at(rows, y), ...(snitt?.length ? { [snittKey]: at(snitt, y) } : {}), Landssnitt: at(land, y) }));
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
        {title} – {suffix}
      </p>
      <div className="h-[240px]" data-testid={testId}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart}>
            {endringsLinjer(chart)}
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
            <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
            <YAxis stroke="#8AA4BF" fontSize={11} tickFormatter={format} width={50} />
            <Tooltip formatter={format} contentStyle={tooltipStyle} />
            <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="Kommunen" stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            {snitt?.length > 0 && <Line type="monotone" dataKey={snittKey} stroke="#3F8A86" strokeWidth={2} dot={{ r: 2 }} connectNulls />}
            <Line type="monotone" dataKey="Landssnitt" stroke="#9CA3AF" strokeWidth={2} dot={{ r: 2 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const ArbeidskapitalChart = ({ data: payload, kommuneName, alertYears }) => {
  const data = payload?.rows;
  if (!data || data.length === 0) return null;
  const averages = payload.averages;
  const g = gruppeInfo(payload);
  const snitt = g.storby ? averages?.gruppe : averages?.fylke;
  const suffix = snitt?.length ? `mot ${g.storby ? "storby- og landssnitt" : "fylkes- og landssnitt"}` : "mot landssnittet";
  const first = data[0];
  const last = data[data.length - 1];
  const fAvg = snitt?.find((r) => r.year === last.year);
  const lAvg = averages?.land?.find((r) => r.year === last.year);
  const chart = data.map((r) => ({ year: r.year, Omløpsmidlar: r.omlopsmidler, "Kortsiktig gjeld": r.kortsiktig_gjeld, Arbeidskapital: r.arbeidskapital, "Utan premieavvik": r.reell_arbeidskapital, pct: r.pct_av_driftsinntekter, reellPct: r.reell_pct }));
  const diff = last.arbeidskapital - first.arbeidskapital;
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="arbeidskapital-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Wallet className="w-5 h-5 text-[#103766]" />
          Arbeidskapital – omløpsmidlar minus kortsiktig gjeld
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Arbeidskapitalen viser kommunens likviditet: kor mykje som er igjen av bankinnskot, plasseringar og fordringar når kortsiktig gjeld er betalt.</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="arbeidskapital-siste">
            <p className="text-xs text-[#8AA4BF]">Arbeidskapital {last.year}</p>
            <p className={`text-2xl font-light ${last.arbeidskapital >= 0 ? "text-[#1F2A36]" : "text-[#C0392B]"}`}>{mill(last.arbeidskapital)}</p>
            <p className="text-[11px] text-[#4B647F]">{formatCurrency(last.arbeidskapital)}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="arbeidskapital-pct">
            <p className="text-xs text-[#8AA4BF]">I prosent av driftsinntektene</p>
            <p className="text-2xl font-light text-[#1F2A36]">{last.pct_av_driftsinntekter == null ? "—" : `${dec(last.pct_av_driftsinntekter, 1)} %`}</p>
            <p className="text-[11px] text-[#4B647F]">{first.pct_av_driftsinntekter != null ? `${dec(first.pct_av_driftsinntekter, 1)} % i ${first.year}` : ""}</p>
            {lAvg && <p className="text-[11px] text-[#3F8A86] mt-1" data-testid="arbeidskapital-pct-avg">{fAvg ? `${g.kort}: ${f1(fAvg.pct_av_driftsinntekter)} % · ` : ""}Landet: {f1(lAvg.pct_av_driftsinntekter)} %</p>}
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="likviditetsgrad">
            <p className="text-xs text-[#8AA4BF]">Likviditetsgrad {last.year}</p>
            <p className={`text-2xl font-light ${last.likviditetsgrad >= 1 ? "text-[#1F2A36]" : "text-[#C0392B]"}`}>{f2(last.likviditetsgrad)}</p>
            <p className="text-[11px] text-[#4B647F]">omløpsmidlar delt på kortsiktig gjeld</p>
            {lAvg && <p className="text-[11px] text-[#3F8A86] mt-1" data-testid="likviditetsgrad-avg">{fAvg ? `${g.kort}: ${f2(fAvg.likviditetsgrad)} · ` : ""}Landet: {f2(lAvg.likviditetsgrad)}</p>}
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="arbeidskapital-endring">
            <p className="text-xs text-[#8AA4BF]">Endring {first.year}–{last.year}</p>
            <p className={`text-2xl font-light ${diff >= 0 ? "text-[#2F7D5B]" : "text-[#C0392B]"}`}>{diff >= 0 ? "+" : "−"}{mill(Math.abs(diff))}</p>
            <p className="text-[11px] text-[#4B647F]">{first.arbeidskapital ? `${diff >= 0 ? "+" : ""}${((diff / Math.abs(first.arbeidskapital)) * 100).toFixed(0)} %` : ""}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="arbeidskapital-reell">
            <p className="text-xs text-[#8AA4BF]">Utan premieavvik {last.year}</p>
            <p className={`text-2xl font-light ${(last.reell_arbeidskapital ?? 0) >= 0 ? "text-[#1F2A36]" : "text-[#C0392B]"}`}>{mill(last.reell_arbeidskapital)}</p>
            <p className="text-[11px] text-[#4B647F]">
              {last.reell_pct == null ? "arbeidskapital minus premieavvik" : `${f1(last.reell_pct)} % av driftsinntektene`}
            </p>
            {lAvg?.reell_pct != null && (
              <p className="text-[11px] text-[#3F8A86] mt-1" data-testid="arbeidskapital-reell-avg">
                {fAvg?.reell_pct != null ? `${g.kort}: ${f1(fAvg.reell_pct)} % · ` : ""}Landet: {f1(lAvg.reell_pct)} %
              </p>
            )}
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="arbeidskapital-deler">
            <p className="text-xs text-[#8AA4BF]">Omløpsmidlar / kortsiktig gjeld {last.year}</p>
            <p className="text-2xl font-light text-[#1F2A36]">{mill(last.omlopsmidler)}</p>
            <p className="text-[11px] text-[#4B647F]">minus {mill(last.kortsiktig_gjeld)}</p>
          </div>
        </div>
        <div className="h-[340px]" data-testid="arbeidskapital-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart} barCategoryGap="30%">
              {endringsLinjer(chart)}
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} width={90} tickFormatter={(v) => `${formatNumber(Math.round(v / 1000))} mill.`} />
              <Tooltip
                formatter={(v, name, p) => {
                  if (name === "Arbeidskapital" && p.payload.pct != null) return `${formatCurrency(v)} (${dec(p.payload.pct, 1)} % av driftsinntektene)`;
                  if (name === "Utan premieavvik" && p.payload.reellPct != null) return `${formatCurrency(v)} (${dec(p.payload.reellPct, 1)} % av driftsinntektene)`;
                  return formatCurrency(v);
                }}
                contentStyle={tooltipStyle}
              />
              <Legend itemSorter={null} />
              <ReferenceLine y={0} stroke="#4B647F" />
              {(alertYears || []).map((y) => (
                <ReferenceLine key={y} x={y} stroke="#C0392B" strokeDasharray="4 3" label={{ value: "Kraftig fall", position: "top", fontSize: 10, fill: "#C0392B" }} />
              ))}
              <Bar dataKey="Omløpsmidlar" fill="#3F8A86" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Kortsiktig gjeld" fill="#D35E4E" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="Arbeidskapital" stroke="#103766" strokeWidth={3} dot={{ r: 4, fill: "#103766" }} />
              <Line type="monotone" dataKey="Utan premieavvik" stroke="#C0392B" strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3, fill: "#C0392B" }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <AvgLines rows={data} land={averages?.land} snitt={snitt} snittKey={g.key} suffix={suffix} field="pct_av_driftsinntekter" format={(v) => (v == null ? "—" : `${f1(v)} %`)} testId="arbeidskapital-pct-chart" title="Arbeidskapital i prosent av driftsinntektene" />
          <AvgLines rows={data} land={averages?.land} snitt={snitt} snittKey={g.key} suffix={suffix} field="likviditetsgrad" format={f2} testId="likviditetsgrad-chart" title="Likviditetsgrad (omløpsmidlar / kortsiktig gjeld)" />
          <AvgLines rows={data} land={averages?.land} snitt={snitt} snittKey={g.key} suffix={suffix} field="reell_pct" format={(v) => (v == null ? "—" : `${f1(v)} %`)} testId="reell-arbeidskapital-chart" title="Reell arbeidskapital (utan premieavvik) i prosent av driftsinntektene" />
        </div>
        <p className="text-xs text-[#8AA4BF]">
          Snitta er uvekta gjennomsnitt av kommunane sine verdiar{lAvg ? ` (${fAvg ? `${fAvg.kommune_count} kommunar i ${g.ord}, ` : ""}${lAvg.kommune_count} i landet)` : ""}. Likviditetsgrad over 1 tyder at omløpsmidlane dekkjer den kortsiktige gjelda.
        </p>
        <p className="text-xs text-[#8AA4BF]">
          Kjelde: SSB <a href="https://www.ssb.no/statbank/table/13545" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">13545 <ExternalLink className="w-3 h-3" /></a> – balanserekneskap, kommunekasse (1000 kr). B. Omløpsmidlar (inkl. premieavvik) minus E. Kortsiktig gjeld. Premieavviket (KG61) er utsett pensjonskostnad som ligg i omløpsmidlane, men som må førast som kostnad seinare år – difor viser vi også arbeidskapitalen med premieavviket trekt frå.
        </p>
      </CardContent>
    </Card>
  );
};

export default ArbeidskapitalChart;
